const apiBase = import.meta.env.VITE_CRM_API_BASE_URL ?? "/api/v1";

export interface AIProviderStatus {
  provider: string;
  model: string;
  expectedModelVersion: string;
  allowProviderFallback: false;
  allowModelFallback: false;
  statuses: readonly {
    key: string;
    testOnly: boolean;
    health: { available: boolean; capabilities: readonly string[] };
  }[];
}

export interface AIUseCase {
  code: string;
  name: string;
  businessDomain: string;
  model: string;
  provider: string;
  thinking: "disabled" | "enabled";
  reasoningEffort: "high" | "max";
  executionMode: "DIRECT_STRICT" | "THINK_THEN_STRICT" | "TEXT_ONLY";
  providerSchemaVersion: string;
  internalSchemaVersion: string;
  humanReviewRequired: boolean;
}

export interface AIUsageRecord {
  passType: "DIRECT" | "ANALYSIS" | "PROJECTION";
  providerAttempt: number;
  inputUnits: number | null;
  outputUnits: number | null;
  cacheHitUnits: number | null;
  estimatedCost: number | string | null;
  latencyMs: number | null;
  validationStatus: string;
}

export interface AIRunView {
  invocationId: string;
  logicalRunId: string;
  executionMode: "DIRECT_STRICT" | "THINK_THEN_STRICT" | "TEXT_ONLY";
  providerRequestCount: number;
  finalSchemaStatus: "PENDING" | "VALID" | "FAILED";
  model: "deepseek-v4-flash";
  expectedModelVersion: "DeepSeek-V4-Flash-0731";
  status: string;
  integrationStatus:
    | "PENDING"
    | "RUNNING"
    | "SUCCEEDED"
    | "REVIEW_REQUIRED"
    | "FAILED"
    | "CANCELLED";
  advisory: true;
  humanReviewStatus: string;
  structuredOutput?: Record<string, unknown>;
  usageRecords?: readonly AIUsageRecord[];
}

export interface AIRunRequest {
  useCaseKey: string;
  workspaceId: string;
  subjectId: string;
  input: Record<string, unknown>;
}

export type AIReviewDecision =
  | "ACCEPT"
  | "ACCEPT_WITH_CHANGES"
  | "REJECT"
  | "RETRY";

export async function loadProviderStatus(
  signal?: AbortSignal,
): Promise<AIProviderStatus> {
  const response = await fetch(`${apiBase}/ai/providers/status`, {
    credentials: "include",
    signal,
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    throw new Error(`AI 服务商状态暂时不可用（HTTP ${response.status}）`);
  return response.json() as Promise<AIProviderStatus>;
}

export async function loadUseCases(
  signal?: AbortSignal,
): Promise<readonly AIUseCase[]> {
  const response = await fetch(`${apiBase}/ai/use-cases`, {
    credentials: "include",
    signal,
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    throw new Error(`AI 用例目录暂时不可用（HTTP ${response.status}）`);
  const body = (await response.json()) as { useCases: readonly AIUseCase[] };
  return body.useCases;
}

export async function createAIRun(input: AIRunRequest): Promise<AIRunView> {
  const csrf = csrfToken();
  const response = await fetch(`${apiBase}/ai/runs`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-csrf-token": csrf,
      "x-idempotency-key": `ai-workbench-${crypto.randomUUID()}`,
    },
    body: JSON.stringify(runBody(input)),
  });
  if (!response.ok) throw new Error(`AI 运行被拒绝（HTTP ${response.status}）`);
  return response.json() as Promise<AIRunView>;
}

export async function retryAIRun(
  invocationId: string,
  input: AIRunRequest,
): Promise<AIRunView> {
  const csrf = csrfToken();
  const response = await fetch(`${apiBase}/ai/runs/${invocationId}/retry`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-csrf-token": csrf,
      "x-idempotency-key": `ai-workbench-retry-${crypto.randomUUID()}`,
    },
    body: JSON.stringify(runBody(input)),
  });
  if (!response.ok) throw new Error(`AI 重试被拒绝（HTTP ${response.status}）`);
  return response.json() as Promise<AIRunView>;
}

export async function reviewAIRun(
  invocationId: string,
  decision: Exclude<AIReviewDecision, "RETRY">,
  changes: Record<string, unknown> = {},
): Promise<void> {
  const csrf = csrfToken();
  const response = await fetch(`${apiBase}/ai/runs/${invocationId}/reviews`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-csrf-token": csrf,
    },
    body: JSON.stringify({
      decision,
      changes,
      reason: "Permissioned manual review from Limited AI Workbench",
    }),
  });
  if (!response.ok) throw new Error(`AI 复核被拒绝（HTTP ${response.status}）`);
}

export async function loadAIRun(
  invocationId: string,
  signal?: AbortSignal,
): Promise<AIRunView> {
  const response = await fetch(`${apiBase}/ai/runs/${invocationId}`, {
    credentials: "include",
    signal,
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    throw new Error(`AI 运行状态暂时不可用（HTTP ${response.status}）`);
  const raw = (await response.json()) as Record<string, unknown>;
  const artifacts = Array.isArray(raw.outputArtifacts)
    ? (raw.outputArtifacts as readonly Record<string, unknown>[])
    : [];
  const usageRecords = Array.isArray(raw.usageRecords)
    ? (raw.usageRecords as unknown as readonly AIUsageRecord[])
    : [];
  const artifact = artifacts[0];
  return {
    invocationId: String(raw.id ?? invocationId),
    logicalRunId: String(raw.logicalRunId ?? raw.id ?? invocationId),
    executionMode: executionMode(raw.executionMode),
    providerRequestCount: usageRecords.length,
    finalSchemaStatus:
      artifact?.validationStatus === "VALID"
        ? "VALID"
        : raw.status === "FAILED"
          ? "FAILED"
          : "PENDING",
    model: "deepseek-v4-flash",
    expectedModelVersion: "DeepSeek-V4-Flash-0731",
    status: String(raw.status ?? "UNKNOWN"),
    integrationStatus: integrationStatus(raw.integrationStatus, raw.status),
    advisory: true,
    humanReviewStatus: String(raw.humanReviewStatus ?? "PENDING"),
    ...(isRecord(artifact?.structuredOutput)
      ? { structuredOutput: artifact.structuredOutput }
      : {}),
    usageRecords,
  };
}

function runBody(input: AIRunRequest): Record<string, unknown> {
  return {
    workspaceId: input.workspaceId,
    useCaseKey: input.useCaseKey,
    subject: { type: "InboundInquiry", id: input.subjectId },
    subjectReferences: [{ type: "InboundInquiry", id: input.subjectId }],
    input: input.input,
    executionMode: "async",
    idempotencyKey: `workbench-${crypto.randomUUID()}`,
    requestedSchemaVersion: "1",
  };
}

function csrfToken(): string {
  const csrf = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("crm_session_csrf="))
    ?.split("=")[1];
  if (!csrf) throw new Error("会话已过期；请重新登录后再执行人工操作。");
  return csrf;
}

function integrationStatus(
  value: unknown,
  lifecycle: unknown,
): AIRunView["integrationStatus"] {
  if (
    value === "PENDING" ||
    value === "RUNNING" ||
    value === "SUCCEEDED" ||
    value === "REVIEW_REQUIRED" ||
    value === "FAILED" ||
    value === "CANCELLED"
  )
    return value;
  if (lifecycle === "RUNNING") return "RUNNING";
  if (lifecycle === "FAILED") return "FAILED";
  if (lifecycle === "CANCELLED") return "CANCELLED";
  if (lifecycle === "COMPLETED") return "REVIEW_REQUIRED";
  return "PENDING";
}

function executionMode(value: unknown): AIRunView["executionMode"] {
  return value === "DIRECT_STRICT" ||
    value === "THINK_THEN_STRICT" ||
    value === "TEXT_ONLY"
    ? value
    : "DIRECT_STRICT";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
